import SearchIcon from '@mui/icons-material/Search';
import { CircularProgress, InputAdornment, MenuItem, SelectChangeEvent, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import useInfiniteScroll from '../../../../../hooks/useInfiniteScroll';
import { IGenericAutocompleteConfig, IStaticOption, UseInfiniteScrollHook } from '../../../../../types/tables';
import {
  ClearIconWrapper,
  DEBOUNCE_DELAY,
  DisplayLabelText,
  FilterWrapper,
  getSelectSx,
  HoverOverlay,
  ItemContentWrapper,
  ItemImage,
  ItemName,
  ItemSecondaryText,
  ItemTextWrapper,
  LoaderWrapper,
  MenuPaperSx,
  SearchContainer,
  SearchTextField,
  StyledClearIcon,
  StyledMenuItem,
  StyledSelect,
} from './InfiniteScrollSelect.styles';

interface IGenericAutocompleteProps<T, P extends { search_query?: string }> {
  label: string;
  value: string | undefined;
  onChange: (value: string | string[] | undefined) => void;
  onClear: (onChange: (value: string | string[] | undefined) => void) => void;
  onNameChange?: (name: string | undefined) => void;
  displayName?: string | undefined;
  isIconVisible?: boolean;
  config: IGenericAutocompleteConfig<T>;
  useDataHook: UseInfiniteScrollHook<T, P>;
  dataAction?: string;
  staticOptions?: IStaticOption[];
}

export const InfiniteScrollSelect = <T, P extends { search_query?: string; page_size?: number }>({
  label,
  value,
  onChange,
  onClear,
  onNameChange,
  displayName,
  isIconVisible,
  config,
  useDataHook,
  dataAction,
  staticOptions = [],
}: IGenericAutocompleteProps<T, P>) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const hasValue = useMemo(() => !!value, [value]);

  const queryParams = useMemo(
    () =>
      ({
        page_size: config.maxItems,
        ...(debouncedSearchQuery.trim() && { search_query: debouncedSearchQuery.trim() }),
      } as P),
    [config.maxItems, debouncedSearchQuery],
  );

  const {
    data: itemsData,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useDataHook(queryParams, open);

  const items = useMemo<T[]>(() => (itemsData?.pages.flat() as T[]) || [], [itemsData]);
  const selectSx = useMemo(() => getSelectSx(value), [value]);

  const shouldShowHiddenMenuItem = useMemo(() => {
    return value && !items.find((item) => config.getId(item) === value);
  }, [value, items, config]);

  const handleScroll = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    threshold: 0.7,
  });

  const renderDisplayLabel = useCallback(() => {
    if (!value) {
      return label;
    }

    // Check static options first
    const staticOption = staticOptions.find((option) => option.id === value);
    if (staticOption) {
      return <DisplayLabelText>{staticOption.name}</DisplayLabelText>;
    }

    const foundItem = items.find((item) => config.getId(item) === value);
    const itemName = displayName || (foundItem ? config.getName(foundItem) : undefined);

    return itemName ? <DisplayLabelText>{itemName}</DisplayLabelText> : label;
  }, [value, label, displayName, items, config, staticOptions]);

  const handleChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const newValue = event.target.value;
      onChange(newValue as string);

      if (onNameChange) {
        const staticOption = staticOptions.find((option) => option.id === newValue);
        if (staticOption) {
          onNameChange(staticOption.name);
        } else {
          const selectedItem = items.find((item) => config.getId(item) === newValue);
          onNameChange(selectedItem ? config.getName(selectedItem) : undefined);
        }
      }

      setOpen(false);
    },
    [onChange, onNameChange, items, config, staticOptions],
  );

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearchQuery('');
    setDebouncedSearchQuery('');
  }, []);

  const handleClearClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClear(onChange);

      if (onNameChange) {
        onNameChange(undefined);
      }

      setOpen(false);
    },
    [onClear, onChange, onNameChange],
  );

  const handleStopPropagation = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  return (
    <FilterWrapper>
      <HoverOverlay className="hover-overlay" />
      <StyledSelect
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        value={value || ''}
        onChange={handleChange}
        displayEmpty
        renderValue={renderDisplayLabel}
        disableUnderline
        variant="standard"
        data-action={dataAction}
        MenuProps={{
          PaperProps: {
            sx: MenuPaperSx,
            onScroll: handleScroll,
          },
        }}
        sx={selectSx}
      >
        <SearchContainer>
          <SearchTextField
            fullWidth
            size="small"
            placeholder="Search"
            value={searchQuery}
            onChange={handleSearchChange}
            onClick={handleStopPropagation}
            onKeyDown={handleStopPropagation}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ fontSize: 24, color: 'action.active' }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </SearchContainer>
        {/* needed to hide the console error 'You have provided an out-of-range value for the select component'
        when option was selected through the search */}
        {shouldShowHiddenMenuItem && <MenuItem value={value} sx={{ display: 'none' }} />}
        {(!isFetching || items.length > 0) &&
          staticOptions.map((option) => (
            <StyledMenuItem key={option.id} value={option.id} selected={value === option.id}>
              <ItemContentWrapper>
                <ItemTextWrapper>
                  <ItemName>{option.name}</ItemName>
                </ItemTextWrapper>
              </ItemContentWrapper>
            </StyledMenuItem>
          ))}
        {items.map((item) => {
          const id = config.getId(item);
          const name = config.getName(item);
          const imageUrl = config.getImageUrl(item);
          const secondaryText = config.getSecondaryText?.(item);

          return (
            <StyledMenuItem key={id} value={id} selected={value === id}>
              <ItemContentWrapper>
                {isIconVisible && <ItemImage src={imageUrl} alt={name} loading="lazy" />}
                <ItemTextWrapper>
                  <ItemName>{name}</ItemName>
                  {secondaryText && <ItemSecondaryText>{secondaryText}</ItemSecondaryText>}
                </ItemTextWrapper>
              </ItemContentWrapper>
            </StyledMenuItem>
          );
        })}
        {isFetching && (
          <LoaderWrapper>
            <CircularProgress size={20} />
          </LoaderWrapper>
        )}
        {!isFetching && items.length === 0 && (
          <MenuItem disabled>
            <Typography>No items found</Typography>
          </MenuItem>
        )}
      </StyledSelect>
      {hasValue && (
        <ClearIconWrapper onClick={handleClearClick}>
          <StyledClearIcon />
        </ClearIconWrapper>
      )}
    </FilterWrapper>
  );
};
